import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL, fetchWithCsrf } from '../../../config/api';


/**
 * Domain Explorer (admin). Browse the corpus by rating, search by name, open a
 * row to see the rating's one-line reason and the fired flags grouped by tier.
 * Any domain, including one not yet in the corpus, can be queued for a scan.
 */
const LEVELS = ['legit', 'low', 'elevated', 'high'];
const LEVEL_TONE = {
  legit: 'bg-green-600', low: 'bg-blue-600', elevated: 'bg-orange-500', high: 'bg-red-600',
};
const TIER_LABEL = { high: 'High', elevated: 'Elevated', low: 'Low', informational: 'Informational (not scored)' };
const TIER_TONE = {
  high: 'text-red-700', elevated: 'text-orange-600', low: 'text-gray-600', informational: 'text-gray-400 italic',
};

const num = (n) => (n == null ? '—' : Number(n).toLocaleString());
const ago = (iso) => {
  if (!iso) return '—';
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  return d === 0 ? 'today' : `${d}d ago`;
};

// Column -> the direction that is most useful on the first click.
const SORT_DEFAULT_DIR = { domain: 'asc', rating: 'desc', age: 'desc', rank: 'asc', flags: 'desc', scanned: 'desc' };

export default function DomainExplorer() {
  const [level, setLevel] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('domain');
  const [dir, setDir] = useState('asc');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);      // domain name
  const [detail, setDetail] = useState(null);  // inspect payload
  const [detailMsg, setDetailMsg] = useState(null);
  const inflight = useRef(null); // AbortController of the newest list request

  const load = useCallback(() => {
    // Only the newest request may update the table. Clicking a rating while a
    // slow page is still loading used to let the older response land second
    // and overwrite the newer one ("glitchy" sort): abort the old request and
    // ignore anything it still manages to return.
    if (inflight.current) inflight.current.abort();
    const ctrl = new AbortController();
    inflight.current = ctrl;
    setLoading(true);
    const params = new URLSearchParams({ page, pageSize: 50, sort, dir });
    if (level) params.set('level', level);
    if (q.trim()) params.set('q', q.trim());
    fetch(`${API_BASE_URL}/bff/admin/domains?${params}`, { credentials: 'include', signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (inflight.current === ctrl) setData(d); })
      .catch((e) => { if (e.name !== 'AbortError' && inflight.current === ctrl) setData(null); })
      .finally(() => { if (inflight.current === ctrl) setLoading(false); });
  }, [level, q, page, sort, dir]);
  useEffect(() => { load(); }, [load]);

  // Click a header: same column flips direction, a new column starts on its
  // natural direction. Always back to page 1 so the view can't land on a page
  // that no longer exists under the new order.
  const toggleSort = (key) => {
    if (sort === key) setDir(dir === 'asc' ? 'desc' : 'asc');
    else { setSort(key); setDir(SORT_DEFAULT_DIR[key] || 'asc'); }
    setPage(1);
  };
  const SortTh = ({ col, children, right }) => (
    <th className={`py-1.5 px-2 ${right ? 'text-right' : ''}`}>
      <button type="button" onClick={() => toggleSort(col)}
        className={`inline-flex items-center gap-1 hover:text-black ${sort === col ? 'text-black font-semibold' : ''}`}
        title={`Sort by ${children}`}>
        {children}
        <span className="text-[10px] w-2">{sort === col ? (dir === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );

  const openRow = useCallback(async (domain) => {
    setOpen(domain); setDetail(null); setDetailMsg(null);
    try {
      const r = await fetch(`${API_BASE_URL}/bff/admin/domains/inspect?domain=${encodeURIComponent(domain)}`, { credentials: 'include' });
      if (r.status === 404) { setDetailMsg('not_in_corpus'); return; }
      if (r.status === 400) { setDetailMsg('Not a valid domain name.'); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDetail(await r.json());
    } catch (e) {
      setDetailMsg(e.message || 'Could not load this domain.');
    }
  }, []);

  const queueScan = useCallback(async (domain) => {
    try {
      const r = await fetchWithCsrf(`${API_BASE_URL}/bff/admin/rerank`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain, reason: 'Domain Explorer: inspect new domain' }),
      });
      if (r.status === 409) { setDetailMsg('Already queued — check back in a minute.'); return; }
      if (r.status === 400) { setDetailMsg('Not a valid domain name.'); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDetailMsg('Queued. The scanner picks new domains up within a minute; reopen this row to see the result.');
    } catch (e) {
      setDetailMsg(e.message || 'Could not queue the scan.');
    }
  }, []);

  const totals = data?.totals || {};
  const grand = LEVELS.reduce((s, l) => s + (totals[l] || 0), 0);
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div>
      {/* Rating bar: proportional segments, click to filter */}
      {grand > 0 && (
        <div className="mb-3">
          <div className="flex h-5 rounded-full overflow-hidden border border-gray-200">
            {LEVELS.map((l) => (
              <button
                key={l}
                type="button"
                title={`${l}: ${num(totals[l])} (${((100 * (totals[l] || 0)) / grand).toFixed(1)}%)`}
                onClick={() => { setLevel(level === l ? '' : l); setPage(1); }}
                className={`${LEVEL_TONE[l]} ${level && level !== l ? 'opacity-30' : ''} transition-opacity`}
                style={{ width: `${(100 * (totals[l] || 0)) / grand}%` }}
              />
            ))}
          </div>
          <div className="flex gap-4 mt-1.5 text-xs text-gray-600 flex-wrap">
            {LEVELS.map((l) => (
              <button key={l} type="button" onClick={() => { setLevel(level === l ? '' : l); setPage(1); }}
                className={`capitalize ${level === l ? 'font-semibold text-black' : ''}`}>
                <span className={`inline-block w-2 h-2 rounded-full mr-1 ${LEVEL_TONE[l]}`} />
                {l} {num(totals[l])} ({((100 * (totals[l] || 0)) / grand).toFixed(1)}%)
              </button>
            ))}
            {level && <button type="button" className="text-blue-700" onClick={() => { setLevel(''); setPage(1); }}>show all</button>}
          </div>
        </div>
      )}

      <form className="flex gap-2 mb-3" onSubmit={(e) => { e.preventDefault(); setPage(1); }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search or type any domain, e.g. example.com"
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
        />
        <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Search</button>
        {q.trim() && (
          <button type="button" onClick={() => openRow(q.trim().toLowerCase())}
            className="border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium" title="Open this exact domain, even if it is not in the corpus yet">
            Inspect
          </button>
        )}
      </form>

      <div className="grid md:grid-cols-5 gap-4">
        <div className={`${open ? 'md:col-span-3' : 'md:col-span-5'} overflow-y-auto max-h-[32rem] border border-gray-100 rounded-lg`}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 text-left text-gray-400">
              <tr>
                <SortTh col="domain">Domain</SortTh>
                <SortTh col="rating">Rating</SortTh>
                <SortTh col="age" right>Age (days)</SortTh>
                <SortTh col="rank" right>Rank</SortTh>
                <SortTh col="flags" right>Flags</SortTh>
                <SortTh col="scanned" right>Scanned</SortTh>
              </tr>
            </thead>
            <tbody>
              {loading && !data && <tr><td className="p-3 text-gray-400" colSpan={6}>Loading…</td></tr>}
              {data?.items?.length === 0 && <tr><td className="p-3 text-gray-400" colSpan={6}>No domains match.</td></tr>}
              {data?.items?.map((d) => (
                <tr key={d.domain}
                  className={`border-t border-gray-50 cursor-pointer hover:bg-gray-50 ${open === d.domain ? 'bg-blue-50' : ''}`}
                  onClick={() => openRow(d.domain)}>
                  <td className="py-1 px-2 font-medium text-black truncate max-w-[16rem]">{d.domain}</td>
                  <td className="py-1 px-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs text-white capitalize ${LEVEL_TONE[d.risk_level] || 'bg-gray-400'}`}>{d.risk_level || '—'}</span>
                  </td>
                  <td className="py-1 px-2 text-right tabular-nums text-gray-600">{num(d.age_days)}</td>
                  <td className="py-1 px-2 text-right tabular-nums text-gray-600">{d.majestic_rank ? `#${num(d.majestic_rank)}` : '—'}</td>
                  <td className="py-1 px-2 text-right tabular-nums text-gray-600">{d.flag_count}</td>
                  <td className="py-1 px-2 text-right text-gray-400 whitespace-nowrap">{ago(d.scanned_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && pages > 1 && (
            <div className="flex items-center justify-between px-2 py-1.5 text-xs text-gray-500 border-t border-gray-100 sticky bottom-0 bg-white">
              <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="disabled:opacity-30">‹ prev</button>
              <span>page {page} of {num(pages)} · {num(data.total)} domains</span>
              <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} className="disabled:opacity-30">next ›</button>
            </div>
          )}
        </div>

        {open && (
          <div className="md:col-span-2 border border-gray-200 rounded-lg p-3 text-sm max-h-[32rem] overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-black truncate">{open}</span>
              <button type="button" className="text-gray-400 hover:text-black px-1" onClick={() => setOpen(null)} aria-label="Close">✕</button>
            </div>

            {detailMsg === 'not_in_corpus' && (
              <div>
                <p className="text-gray-600 mb-2">Not in the corpus yet.</p>
                <button type="button" onClick={() => queueScan(open)} className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium">
                  Queue a scan
                </button>
              </div>
            )}
            {detailMsg && detailMsg !== 'not_in_corpus' && <p className="text-gray-600">{detailMsg}</p>}
            {!detail && !detailMsg && <p className="text-gray-400">Loading…</p>}

            {detail && (
              <>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs text-white capitalize ${LEVEL_TONE[detail.assessment?.risk_level] || 'bg-gray-400'}`}>
                    {detail.assessment?.risk_level || 'unrated'}
                  </span>
                  {detail.mirror?.majestic_rank != null && <span className="text-xs text-gray-500">majestic #{num(detail.mirror.majestic_rank)}</span>}
                  {detail.assessment?.created_date && <span className="text-xs text-gray-500">registered {new Date(detail.assessment.created_date).toLocaleDateString()}</span>}
                  {detail.assessment?.scanned_at && <span className="text-xs text-gray-400">scanned {ago(detail.assessment.scanned_at)}</span>}
                </div>

                <div className="rounded-lg bg-gray-50 border border-gray-100 p-2 mb-3">
                  <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-0.5">Why this rating</div>
                  <div className="text-black">{detail.assessment?.reason || 'Reason not stored for this scan yet (re-assess to populate).'}</div>
                </div>

                {['high', 'elevated', 'low', 'informational'].map((tier) => {
                  const names = detail.flagsByTier?.[tier] || [];
                  if (!names.length) return null;
                  return (
                    <div key={tier} className="mb-2">
                      <div className={`text-[11px] uppercase tracking-wide mb-0.5 ${TIER_TONE[tier]}`}>{TIER_LABEL[tier]} · {names.length}</div>
                      <div className="flex flex-wrap gap-1">
                        {names.map((n) => (
                          <span key={n} className={`px-1.5 py-0.5 rounded border border-gray-200 bg-white text-[11px] ${tier === 'informational' ? 'text-gray-400' : 'text-gray-700'}`}>{n}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {(detail.assessment?.legit_signals || []).length > 0 && (
                  <div className="mb-2">
                    <div className="text-[11px] uppercase tracking-wide text-green-700 mb-0.5">Positive signals</div>
                    <div className="flex flex-wrap gap-1">
                      {detail.assessment.legit_signals.map((n) => (
                        <span key={n} className="px-1.5 py-0.5 rounded border border-green-200 bg-green-50 text-[11px] text-green-800">{n}</span>
                      ))}
                    </div>
                  </div>
                )}

                {detail.collectorFailures?.length > 0 && (
                  <div className="text-[11px] text-gray-400 mt-2">
                    Collector failures on this scan: {detail.collectorFailures.map((f) => `${f.collector_name} ×${f.n}`).join(', ')}
                  </div>
                )}

                <button type="button" onClick={() => queueScan(open)} className="mt-3 border border-gray-300 px-3 py-1.5 rounded-lg text-xs font-medium">
                  Re-scan now
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
